/**
 * Read a Server-Sent Events stream with `fetch`, so the bearer travels in
 * the `Authorization` header.
 *
 * Browser `EventSource` cannot send headers, which forced the token into
 * the URL (`?access_token=…`) — and URLs land in server access logs and
 * proxy logs. The backend no longer accepts that (RW-1.13).
 */

export interface SseEvent {
  event: string;
  data: string;
}

/**
 * Parse SSE text into events. Returns the parsed events and the trailing
 * partial block to prepend to the next chunk. Comment lines (`: heartbeat`)
 * are skipped; multi-line `data:` fields are joined with `\n`.
 */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const blocks = buffer.replace(/\r\n/g, "\n").split("\n\n");
  const rest = blocks.pop() ?? "";
  const events: SseEvent[] = [];
  for (const block of blocks) {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split("\n")) {
      if (!line || line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
      if (field === "event") event = value;
      else if (field === "data") data.push(value);
    }
    if (data.length > 0) events.push({ event, data: data.join("\n") });
  }
  return { events, rest };
}

/**
 * Stream events from `url` until the response ends or `signal` aborts.
 * Resolves when the stream closes; rejects on a non-2xx response or a
 * transport error, so the caller can reconnect with back-off.
 */
export async function readSse(
  url: string,
  token: string | null,
  signal: AbortSignal,
  onOpen: () => void,
  onEvent: (e: SseEvent) => void,
): Promise<void> {
  const res = await fetch(url, {
    headers: {
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });
  if (!res.ok || !res.body) {
    throw new Error(`event stream: HTTP ${res.status}`);
  }
  onOpen();
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSse(buffer);
    buffer = rest;
    for (const e of events) onEvent(e);
  }
}
